/* BloxShop Theme JS v2 — Full AJAX cart, quick-add, search, modals */
'use strict';

/* ─────────────────────────────────────────
   UTILITIES
───────────────────────────────────────── */
/*
  Renders cents using the shop's own Admin-configured money format (Settings
  > General > Store currency > Change formatting), the same template Liquid's
  `| money` filter uses. That format already wraps output in
  <span class="money">...</span> — the DOM convention currency-conversion
  apps (including the installed Nova app) scan for — so every client-built
  price now carries the same markup a server-rendered Liquid price does,
  with no hardcoded currency symbol and no change to the underlying number.
*/
function bsFormatWithDelimiters(cents, precision, thousands, decimal) {
  precision = precision == null ? 2 : precision;
  thousands = thousands == null ? ',' : thousands;
  decimal = decimal == null ? '.' : decimal;
  cents = isNaN(cents) || cents == null ? 0 : cents;
  var value = (cents / 100).toFixed(precision);
  var parts = value.split('.');
  var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
  var centsPart = parts[1] ? decimal + parts[1] : '';
  return dollars + centsPart;
}
function money(cents) {
  cents = parseInt(cents, 10) || 0;
  var format = (window.BSLocale && window.BSLocale.moneyFormat) || '${{amount}}';
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var match = format.match(placeholderRegex);
  if (!match) return '$' + (cents / 100).toFixed(2);
  var value;
  switch (match[1]) {
    case 'amount_no_decimals':
      value = bsFormatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = bsFormatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = bsFormatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = bsFormatWithDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = bsFormatWithDelimiters(cents, 2);
  }
  return format.replace(placeholderRegex, value);
}
function esc(str) {
  return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

/* ─────────────────────────────────────────
   NOVA CURRENCY COMPATIBILITY ADAPTER
   Nova (the installed multi-currency app, "Nova ‑ Multi Currency Converter")
   owns all FX conversion and currency display — this module never
   calculates, fetches, or stores exchange rates. Its only job is to give
   Nova's own DOM scanner a clear signal after BloxShop replaces or inserts
   money markup client-side (Featured sort, search, subcollection swap, cart
   drawer, inline variant / Quick Add / product-page price updates), since
   content that never existed at initial page load can't have been seen by
   whatever pass Nova already ran.

   Verified directly against the live storefront runtime (window.baCurr —
   Nova is built on the Booster Currency Converter engine):
     - Nova does NOT use a live MutationObserver against new DOM content; it
       scans on a timer/at load and marks each converted node with a
       `bacurr-money` class so repeat scans skip it (confirmed idempotent —
       calling refreshConversion() twice in a row does not re-convert
       already-converted nodes, so this is safe to call liberally and never
       double-converts a value that's already display-converted).
     - window.baCurr.refreshConversion() is Nova's own exposed re-scan
       trigger (its entire body is `window.baCurr.rePeat()`, the same
       function the app's own timer calls) — this is what actually made the
       cart drawer's correctly-marked-up `.money` nodes convert in a live
       test; it was never a markup problem, Nova simply never re-scanned
       after the drawer's AJAX render.
     - window.Nova was never a real global on this store; the old
       candidate-method-name loop (Nova.refresh/reprocess/update/scan/
       convert/refreshPrices) could never have run.
───────────────────────────────────────── */
const BSNovaCurrency = (() => {
  function detect() {
    return !!(window.baCurr || document.querySelector('[id*="nova" i],[class*="nova-currency" i],[data-nova]'));
  }

  function notifyDomUpdated(root) {
    try {
      document.dispatchEvent(new CustomEvent('bs:dynamic-content-updated', { detail: { root: root || document.body } }));
    } catch (e) { /* CustomEvent unsupported in this browser — nothing to fall back to */ }

    if (window.baCurr && typeof window.baCurr.refreshConversion === 'function') {
      try { window.baCurr.refreshConversion(); } catch (e) { /* opportunistic only — never block rendering */ }
    } else if (window.baCurr && typeof window.baCurr.rePeat === 'function') {
      try { window.baCurr.rePeat(); } catch (e) { /* opportunistic only — never block rendering */ }
    }
  }

  return { detect, notifyDomUpdated };
})();
window.BSNovaCurrency = BSNovaCurrency;
/*
  Normalizes known storefront destinations against Shopify's active locale
  root. Same-store absolute URLs are normalized; external destinations and
  platform/app-owned paths are retained. Queries and fragments survive.
*/
function bsLocaleUrl(path) {
  var value = String(path == null ? '' : path);
  if (!value || value[0] === '#' || value[0] === '?') return value;
  var config = (window.BSLocale && window.BSLocale.routes) || {};
  var origin = window.location.origin;
  var url;
  try { url = new URL(value, origin); } catch (e) { return value; }
  if (url.origin !== origin && url.origin !== config.origin) return value;
  if (!/^https?:$/.test(url.protocol)) return value;
  var pathname = url.pathname;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || config.root || '/';
  root = String(root).replace(/\/+$/, '');
  var roots = (config.languageRoots || []).concat([root]).map(function (item) {
    return String(item).replace(/\/+$/, '');
  }).filter(Boolean).sort(function (a, b) { return b.length - a.length; });
  roots.some(function (prefix) {
    if (pathname === prefix || pathname.indexOf(prefix + '/') === 0) {
      pathname = pathname.slice(prefix.length) || '/';
      return true;
    }
    return false;
  });
  if (pathname !== '/' && !/^\/(products|collections|pages|policies|blogs|search|cart|checkout|discount)([/.]|$)/.test(pathname)) return value;
  return root + pathname + url.search + url.hash;
}
function bsApiUrl(path) {
  return bsLocaleUrl(path);
}
async function shopifyFetch(url, opts = {}) {
  const res = await fetch(url, {
    ...opts,
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...(opts.headers || {}) }
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.description || err.message || 'Request failed');
  }
  return res.json();
}

/* ─────────────────────────────────────────
   CART
───────────────────────────────────────── */
const BSCart = (() => {
  let isOpen = false;
  // Single-flight refresh: only one canonical fetch+fee-sync+render pipeline
  // runs at a time. Callers that arrive mid-flight reuse it; at most one
  // trailing re-run is queued so state never falls behind the latest request.
  let refreshInFlight = null;
  let refreshQueued = false;

  async function getCart() {
    return shopifyFetch(bsApiUrl('/cart.js'));
  }

  // Customer-facing quantity excludes every line BSServiceFee recognizes as
  // the hidden fee product, so a duplicated/quantity-2 fee line never
  // inflates the number the customer sees.
  function customerItemCount(cart) {
    var hasFee = typeof BSServiceFee !== 'undefined';
    return (cart.items || []).reduce(function (sum, item) {
      return sum + ((hasFee && BSServiceFee.isFeeLine(item)) ? 0 : item.quantity);
    }, 0);
  }

  function updateBadge(cart) {
    const badge = document.getElementById('bs-cart-count');
    if (!badge) return;
    const count = customerItemCount(cart);
    badge.textContent = count;
    if (count > 0) {
      badge.removeAttribute('hidden');
      badge.animate([{transform:'scale(0.5)'},{transform:'scale(1.3)'},{transform:'scale(1)'}], {duration:300});
    } else {
      badge.setAttribute('hidden', '');
    }
  }

  /*
    Cart state machine.
    IMPORTANT: must removeAttribute('hidden') BEFORE setting style.display,
    because [hidden]{display:none!important} in CSS beats inline style.display.
  */
  function cartHide(el) {
    if (!el) return;
    el.setAttribute('hidden', '');
    el.style.display = 'none';
  }
  function cartShow(el, d) {
    if (!el) return;
    el.removeAttribute('hidden');      // remove !important CSS rule first
    el.style.display = d || 'block';   // then set display
  }

  // True once the drawer already has real, currently-visible line items —
  // used so a later failure can leave that content alone instead of
  // replacing it with the empty or error state.
  function hasVisibleItems() {
    var itemsEl = document.getElementById('bs-cart-items');
    return !!(itemsEl && !itemsEl.hasAttribute('hidden') && itemsEl.children.length > 0);
  }

  function setCartState(state, cart) {
    var loadingEl  = document.getElementById('bs-cart-loading');
    var itemsEl    = document.getElementById('bs-cart-items');
    var emptyEl    = document.getElementById('bs-cart-empty');
    var errorEl    = document.getElementById('bs-cart-error');
    var footerEl   = document.getElementById('bs-cart-footer');
    var subtotalEl = document.getElementById('bs-cart-subtotal');

    // Hide all zones first
    cartHide(loadingEl);
    cartHide(itemsEl);
    cartHide(emptyEl);
    cartHide(errorEl);
    cartHide(footerEl);

    if (state === 'loading') {
      cartShow(loadingEl, 'flex');
      return;
    }

    if (state === 'error') {
      cartShow(errorEl, 'flex');
      return;
    }

    // Empty means the canonical cart genuinely has zero customer merchandise
    // (a stray/duplicate service-fee line does not count) — never used to
    // paper over a fetch/render failure.
    if (state === 'empty' || !cart || customerItemCount(cart) === 0) {
      cartShow(emptyEl, 'flex');
      return;
    }

    // state === 'items' with items
    var hasFeeService = typeof BSServiceFee !== 'undefined';
    // Always detect and hide every recognized fee line from the items list,
    // regardless of how many there are — the synchronizer normally leaves
    // exactly one, but the list must never leak a stray duplicate either.
    var displayItems = cart.items.filter(function(i){
      return !(hasFeeService && BSServiceFee.isFeeLine(i));
    });

    // Declared once here (not inside the .map() callback below) because the
    // fee-summary block further down also needs it — a var scoped to the
    // callback only would throw a ReferenceError in strict mode when read
    // from outside it, silently aborting the rest of this render.
    var BSL = window.BSLocale || {};
    var BSLC = BSL.cart || {};

    cartShow(itemsEl, 'flex');
    itemsEl.innerHTML = displayItems.map(function(item) {
      // Service fee lines get special locked rendering
      var isFee = typeof BSServiceFee !== 'undefined' && BSServiceFee.isFeeLine(item);
      var tooltip = isFee ? ((window.BSServiceFeeSettings || {}).tooltip || BSLC.serviceFeeFallback || 'Service fee') : '';
      return '<div class="bs-cart-item' + (isFee ? ' bs-cart-item--fee' : '') + '" data-key="' + esc(item.key) + '">' +
        (isFee ? '' :
          '<div class="bs-cart-item__img">' +
            (item.image ? '<img src="' + item.image + '" alt="' + esc(item.product_title) + '" width="68" height="68" loading="lazy">' : '') +
          '</div>'
        ) +
        '<div class="bs-cart-item__info">' +
          (isFee
            ? '<span class="bs-cart-item__title bs-cart-item__fee-label">' + (BSLC.serviceFeeLabel || 'Service Fee') + ' ' +
                '<span class="bs-fee-tooltip" tabindex="0" aria-label="' + esc(tooltip) + '">' +
                  '<svg width="13" height="13" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
                  '<span class="bs-fee-tooltip__text">' + esc(tooltip) + '</span>' +
                '</span>' +
              '</span>'
            : '<a href="' + esc(bsLocaleUrl(item.url)) + '" class="bs-cart-item__title">' + esc(item.product_title) + '</a>'
          ) +
          ((!isFee && item.variant_title && item.variant_title !== 'Default Title') ? '<p class="bs-cart-item__variant">' + esc(item.variant_title) + '</p>' : '') +
          '<div class="bs-cart-item__row">' +
            '<span class="bs-price' + (isFee ? ' bs-fee-price' : ' bs-price--sale') + '">' + money(item.final_line_price) + '</span>' +
            (isFee ? '' :
              '<div class="bs-cart-item__qty">' +
                '<button class="bs-cart-item__qty-btn" data-change="' + esc(item.key) + '" data-qty="' + (item.quantity - 1) + '" aria-label="' + ((BSL.accessibility && BSL.accessibility.decreaseQuantity) || 'Decrease') + '">&#8722;</button>' +
                '<span class="bs-cart-item__qty-val">' + item.quantity + '</span>' +
                '<button class="bs-cart-item__qty-btn" data-change="' + esc(item.key) + '" data-qty="' + (item.quantity + 1) + '" aria-label="' + ((BSL.accessibility && BSL.accessibility.increaseQuantity) || 'Increase') + '">+</button>' +
              '</div>'
            ) +
          '</div>' +
          (isFee ? '' : '<button class="bs-cart-item__remove" data-change="' + esc(item.key) + '" data-qty="0">' + (BSLC.remove || 'Remove') + '</button>') +
        '</div>' +
      '</div>';
    }).join('');

    cartShow(footerEl, 'block');
    // Attach checkout via event listener (safer than inline onclick for CSP)
    var chkBtn = document.getElementById('bs-checkout-btn');
    if (chkBtn && !chkBtn._bsCheckoutBound) {
      chkBtn._bsCheckoutBound = true;
      chkBtn.addEventListener('click', function(e) {
        e.preventDefault();
        if (typeof BSDiscount !== 'undefined') {
          BSDiscount.checkout();
        } else {
          window.location.href = bsLocaleUrl('/checkout');
        }
      });
    }

    // Footer: fee amount comes only from the canonical cart the synchronizer
    // already reconciled — sum every recognized fee line defensively (it
    // should normally be exactly one at quantity 1) rather than recomputing
    // the percentage ourselves. A genuinely zero sum (no fee line at all)
    // hides the row instead of estimating one.
    var feeCentsTotal = hasFeeService
      ? cart.items.reduce(function(s, i){ return s + (BSServiceFee.isFeeLine(i) ? i.final_line_price : 0); }, 0)
      : 0;
    var feeLineHtml = '';
    if (feeCentsTotal > 0) {
      var feeTip  = ((window.BSServiceFeeSettings || {}).tooltip || BSLC.serviceFeeFallback || 'Service fee helps us keep prices low.');
      var feeRate = ((window.BSServiceFeeSettings || {}).rate || 5);
      feeLineHtml = '<div class="bs-cart-fee-summary">' +
        '<span class="bs-cart-fee-summary__label">' + (BSLC.serviceFeeLabel || 'Service fee') + ' (' + feeRate + '%)' +
        ' <span class="bs-fee-tooltip" tabindex="0">' +
          '<svg width="13" height="13" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
          '<span class="bs-fee-tooltip__text">' + esc(feeTip) + '</span>' +
        '</span></span>' +
        '<span class="bs-cart-fee-summary__amount bs-bidi">' + money(feeCentsTotal) + '</span>' +
      '</div>';
    }
    var feeContainer = document.getElementById('bs-cart-fee-container');
    if (feeContainer) feeContainer.innerHTML = feeLineHtml;

    // Total always renders from Shopify's canonical cart.total_price, for
    // every non-empty cart, regardless of whether a fee row is showing.
    if (subtotalEl) subtotalEl.innerHTML = money(cart.total_price);
    BSNovaCurrency.notifyDomUpdated(document.getElementById('bs-cart-drawer') || itemsEl);
  }

  function renderItems(cart) { setCartState('items', cart); }

  /*
    Canonical pipeline: fetch cart -> sync fee once -> re-fetch only if the
    fee actually changed -> render once -> badge once. Each stage is caught
    separately so a failure after the cart already loaded degrades to
    "keep showing what we fetched" instead of losing everything. Only a
    failure to fetch the cart at all is rethrown, since there is nothing
    usable to fall back to at that point.
  */
  async function _pipeline() {
    var cart;
    try {
      cart = await getCart();
    } catch (e) {
      console.error('BSCart: fetch cart failed', e);
      throw e;
    }

    updateBadge(cart);
    if (isOpen) setCartState('items', cart);

    if (typeof BSServiceFee !== 'undefined' && BSServiceFee.enabled) {
      var changed;
      try {
        changed = await BSServiceFee.update(cart);
      } catch (e) {
        console.error('BSCart: synchronize fee failed', e);
        return cart;
      }
      if (changed) {
        var updatedCart;
        try {
          updatedCart = await getCart();
        } catch (e) {
          console.error('BSCart: fetch updated cart failed', e);
          return cart;
        }
        updateBadge(updatedCart);
        if (isOpen) setCartState('items', updatedCart);
        return updatedCart;
      }
    }
    return cart;
  }

  // Single-flight: a caller that arrives while a pipeline run is already in
  // flight reuses that same promise instead of starting an independent
  // fetch/fee-sync chain. If a caller arrives during the run, exactly one
  // trailing re-run is queued afterward so the final state reflects the
  // latest request without ever running two pipelines concurrently.
  function refresh() {
    if (refreshInFlight) {
      refreshQueued = true;
      return refreshInFlight;
    }
    refreshInFlight = _pipeline()
      .catch(function (e) {
        // Only the "fetch cart" stage rethrows this far — nothing loaded.
        if (isOpen && !hasVisibleItems()) setCartState('error');
        return null;
      })
      .finally(function () {
        refreshInFlight = null;
        if (refreshQueued) {
          refreshQueued = false;
          refresh();
        }
      });
    return refreshInFlight;
  }

  async function change(key, qty) {
    try {
      await shopifyFetch(bsApiUrl('/cart/change.js'), {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: qty })
      });
    } catch (e) {
      console.error('BSCart: change item failed', e);
      return;
    }
    refresh();
  }

  function open() {
    isOpen = true;
    // The mobile drawer shares document.body.style.overflow as its scroll lock,
    // so closing the cart would otherwise unlock the body under an open drawer.
    BSMobileMenu.close(false);
    const drawer  = document.getElementById('bs-cart-drawer');
    const overlay = document.getElementById('bs-cart-overlay');
    if (drawer)  { drawer.classList.add('open');  drawer.removeAttribute('aria-hidden'); drawer.focus(); }
    if (overlay) overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    // Show loading only if no items already rendered
    if (!hasVisibleItems()) setCartState('loading');
    refresh();
    // Restore saved Roblox username
    const usernameInput = document.getElementById('bs-roblox-username');
    if (usernameInput) usernameInput.value = sessionStorage.getItem('bs_roblox_user') || '';
  }

  function close() {
    isOpen = false;
    const drawer  = document.getElementById('bs-cart-drawer');
    const overlay = document.getElementById('bs-cart-overlay');
    if (drawer)  { drawer.classList.remove('open'); drawer.setAttribute('aria-hidden','true'); }
    if (overlay) overlay.classList.remove('open');
    document.body.style.overflow = '';
  }

  // Event delegation for qty/remove buttons
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-change]');
    if (!btn) return;
    change(btn.dataset.change, parseInt(btn.dataset.qty, 10));
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') close();
  });

  return { open, close, change, refresh, updateBadge };
})();

/* ─────────────────────────────────────────
   TOAST
───────────────────────────────────────── */
const BSToast = (() => {
  let timer;
  function show(title) {
    const toast = document.getElementById('bs-toast');
    const prod  = document.getElementById('bs-toast-product');
    if (!toast) return;
    if (prod) prod.textContent = title;
    toast.classList.add('show');
    clearTimeout(timer);
    timer = setTimeout(hide, 4000);
  }
  function hide() {
    const toast = document.getElementById('bs-toast');
    if (toast) toast.classList.remove('show');
  }
  return { show, hide };
})();

/* ─────────────────────────────────────────
   QUICK ADD
───────────────────────────────────────── */
const BSQuickAdd = (() => {
  function setBtn(btn, state, label) {
    if (!btn) return;
    const states = {
      idle:    `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="white" stroke-width="2.5" stroke-linecap="round"/></svg>`,
      loading: `<div class="bs-spinner" style="width:13px;height:13px;border-width:2px;"></div>`,
      success: `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 13l4 4L19 7" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`
    };
    btn.innerHTML = states[state] || states.idle;
    btn.disabled  = state === 'loading';
    btn.classList.toggle('bs-qadd--loading',  state === 'loading');
    btn.classList.toggle('bs-qadd--success',  state === 'success');
    if (label) btn.setAttribute('aria-label', label);
  }

  async function addVariant(variantId, qty, btn, title) {
    setBtn(btn, 'loading');
    try {
      await shopifyFetch(bsApiUrl('/cart/add.js'), {
        method: 'POST',
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: qty || 1 })
      });
      setBtn(btn, 'success');
      BSToast.show(title || ((window.BSLocale && window.BSLocale.product && window.BSLocale.product.genericItem) || 'Item'));
      // BSCart.open() alone runs the single canonical refresh pipeline —
      // calling BSCart.refresh() here too would start a second, competing one.
      BSCart.open();
      setTimeout(() => setBtn(btn, 'idle'), 2000);
    } catch(e) {
      console.error('BSQuickAdd', e);
      setBtn(btn, 'idle');
      alert(e.message || ((window.BSLocale && window.BSLocale.product && window.BSLocale.product.addError) || 'Could not add to cart. Please try again.'));
    }
  }

  // Event delegation — works for dynamically rendered cards too
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    // Product page ATC — use qty input
    if (btn.dataset.pageAtc) {
      const qty = parseInt(document.getElementById('bs-qty')?.value || '1', 10);
      const vid = document.getElementById('bs-product-select')?.value || btn.dataset.variantId;
      await addVariant(vid, qty, btn, btn.dataset.productTitle);
      return;
    }

    const variantCount = parseInt(btn.dataset.variantCount || '1', 10);
    const title = btn.dataset.productTitle || ((window.BSLocale && window.BSLocale.product && window.BSLocale.product.genericItem) || 'Item');

    if (variantCount > 1) {
      // Inline card-variant selection (BSCardVariants) already resolved an
      // exact variant onto this button's dataset — add it directly instead
      // of opening the modal. A card with no inline selector (or one whose
      // current combination couldn't be resolved) never gets this flag, so
      // it still falls through to the modal exactly as before.
      if (btn.dataset.variantResolved === 'true') {
        await addVariant(btn.dataset.variantId, 1, btn, title);
      } else {
        BSVariantModal.open(btn);
      }
    } else {
      await addVariant(btn.dataset.variantId, 1, btn, title);
    }
  });

  return { addVariant, setBtn };
})();

/* ─────────────────────────────────────────
   INLINE CARD-VARIANT SELECTION
   Drives the pill selectors added to eligible collection-card products
   (product-card-custom.liquid + the Featured/search card builder in
   main-collection-product-grid.liquid both emit the same [data-cv-wrapper]
   markup and JSON shape — see Part 10 of the inline-variant spec). One
   delegated listener on document handles every card regardless of how it
   entered the DOM (initial SSR grid, Featured/search rebuild, subcollection
   Ajax swap, pagination) — cards are never individually bound, so nothing
   needs re-init after a DOM replacement and nothing can double-bind.
   Per-card state lives only on that card's own wrapper element
   (wrapper.__bsCvData / wrapper.__bsCvSelected), so selections never leak
   between cards and are naturally discarded when a card is replaced.
───────────────────────────────────────── */
const BSCardVariants = (() => {
  function parseData(wrapper) {
    if (wrapper.__bsCvData) return wrapper.__bsCvData;
    const raw = wrapper.getAttribute('data-cv-data');
    if (!raw) return null;
    try {
      const data = JSON.parse(raw);
      wrapper.__bsCvData = data;
      return data;
    } catch (e) {
      console.error('BSCardVariants: invalid data', e);
      return null;
    }
  }

  // Exact match across every option position the product actually has —
  // positions beyond that are null on both sides (variant.o is padded to 3)
  // so they always compare equal and never block a match.
  function resolveVariant(data, selected) {
    let found = null;
    data.variants.forEach(v => {
      if (found) return;
      const match = [0, 1, 2].every(i => selected[i] == null || v.o[i] === selected[i]);
      if (match) found = v;
    });
    return found;
  }

  // Availability/price-diff for one candidate value in one group, holding
  // every other currently-selected position fixed — the same combination
  // logic product-card-custom.liquid computes at SSR time and the Featured/
  // search builder computes at build time, so all three ways a card can
  // reach the page agree on which values are selectable.
  function valueAvailability(data, groupIndex, value, selected) {
    let hasVariant = false, available = false, diff = 0;
    const base = resolveVariant(data, selected);
    data.variants.forEach(v => {
      if (v.o[groupIndex] !== value) return;
      const otherMatch = [0, 1, 2].every(i => i === groupIndex || selected[i] == null || v.o[i] === selected[i]);
      if (!otherMatch) return;
      hasVariant = true;
      if (v.available) available = true;
      diff = v.price - (base ? base.price : v.price);
    });
    return { disabled: !hasVariant || !available, diff };
  }

  function pillInnerHTML(value, av, data) {
    let html = '<span>' + esc(value) + '</span>';
    if (av.disabled) {
      html += '<span class="bs-sr-only"> — ' + esc(data.unavailableLabel || 'Unavailable') + '</span>';
    }
    if (data.showPriceDiff && av.diff !== 0 && !av.disabled) {
      html += '<span class="bs-cv-pill__diff" aria-hidden="true">' + (av.diff < 0 ? '−' : '+') + money(Math.abs(av.diff)) + '</span>';
    }
    return html;
  }

  function refreshPills(wrapper, data) {
    const selected = wrapper.__bsCvSelected;
    wrapper.querySelectorAll('[data-cv-group]').forEach(groupEl => {
      const groupIndex = parseInt(groupEl.getAttribute('data-cv-group'), 10);
      groupEl.querySelectorAll('.bs-cv-pill').forEach(pill => {
        const value = pill.getAttribute('data-cv-value');
        const isSelected = selected[groupIndex] === value;
        const av = valueAvailability(data, groupIndex, value, selected);
        pill.classList.toggle('bs-cv-pill--selected', isSelected);
        pill.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        pill.classList.toggle('bs-cv-pill--unavailable', av.disabled);
        pill.disabled = av.disabled;
        pill.innerHTML = pillInnerHTML(value, av, data);
      });
    });
  }

  function updatePrice(card, variant) {
    const priceEl = card.querySelector('[data-cv-price]');
    if (!priceEl) return;
    priceEl.innerHTML = variant.compareAtHtml
      ? '<span class="bs-price bs-price--sale">' + variant.priceHtml + '</span><span class="bs-price bs-price--compare">' + variant.compareAtHtml + '</span>'
      : '<span class="bs-price">' + variant.priceHtml + '</span>';
    BSNovaCurrency.notifyDomUpdated(priceEl);
  }

  function updateImage(card, wrapper, variant) {
    if (wrapper.getAttribute('data-cv-update-image') !== 'true') return;
    const img = card.querySelector('.bs-product-card__img');
    if (!img || img.tagName !== 'IMG') return;
    if (img.__bsCvOriginalSrc == null) img.__bsCvOriginalSrc = img.currentSrc || img.src;
    const targetSrc = variant.image || img.__bsCvOriginalSrc;
    if (img.src === targetSrc) return;
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const swap = () => {
      img.src = targetSrc;
      img.classList.remove('bs-product-card__img--cv-fading');
    };
    if (reduceMotion) { swap(); return; }
    img.classList.add('bs-product-card__img--cv-fading');
    setTimeout(swap, 160);
  }

  // Quick Add's own [data-quick-add] delegation (BSQuickAdd, above) reads
  // these dataset fields at click time — this never adds a second cart
  // pipeline, it only keeps that button's target variant in sync with the
  // card's current selection. __originalHTML is the same cache key the
  // bs-qadd-full loading/success patch (later in this file) already reads
  // and refreshes on every real click, so the two never fight over content.
  function updateQuickAdd(card, variant) {
    const btn = card.querySelector('[data-quick-add]');
    if (!btn) return;
    if (!variant) {
      delete btn.dataset.variantResolved;
      return;
    }
    if (btn.__originalHTML == null) btn.__originalHTML = btn.innerHTML;
    btn.dataset.variantId = variant.id;
    btn.dataset.variantResolved = 'true';
    const isFull = btn.classList.contains('bs-qadd-full');
    const soldOutLabel = (window.BSLocale && window.BSLocale.product && window.BSLocale.product.soldOut) || 'Sold Out';
    if (!variant.available) {
      btn.disabled = true;
      btn.classList.add(isFull ? 'bs-qadd-full--sold-out' : 'bs-qadd--sold-out');
      if (isFull) {
        btn.innerHTML = esc(soldOutLabel);
      } else {
        btn.setAttribute('aria-label', soldOutLabel);
      }
    } else {
      btn.disabled = false;
      btn.classList.remove(isFull ? 'bs-qadd-full--sold-out' : 'bs-qadd--sold-out');
      const title = btn.dataset.productTitle || '';
      const addLabelTpl = (window.BSLocale && window.BSLocale.product && window.BSLocale.product.addItemToCartTemplate) || '';
      if (addLabelTpl) btn.setAttribute('aria-label', addLabelTpl.replace('__TITLE__', title));
      if (isFull) btn.innerHTML = btn.__originalHTML;
    }
  }

  function selectValue(pill) {
    if (pill.disabled) return;
    const groupEl = pill.closest('[data-cv-group]');
    const wrapper = pill.closest('[data-cv-wrapper]');
    const card = wrapper && wrapper.closest('.bs-product-card');
    if (!groupEl || !wrapper || !card) return;
    const data = parseData(wrapper);
    if (!data) return;

    if (!wrapper.__bsCvSelected) {
      const initVariant = data.variants.find(v => v.id === data.selectedId) || data.variants[0];
      wrapper.__bsCvSelected = initVariant ? initVariant.o.slice() : [null, null, null];
    }
    const groupIndex = parseInt(groupEl.getAttribute('data-cv-group'), 10);
    wrapper.__bsCvSelected[groupIndex] = pill.getAttribute('data-cv-value');

    refreshPills(wrapper, data);

    const resolved = resolveVariant(data, wrapper.__bsCvSelected);
    if (resolved) {
      updatePrice(card, resolved);
      updateQuickAdd(card, resolved);
      updateImage(card, wrapper, resolved);
    } else {
      // No exact variant for this combination — leave price/image showing
      // the last resolved state, but clear the resolved flag so Quick Add
      // falls back to the variant modal instead of guessing (Part 9).
      updateQuickAdd(card, null);
    }
  }

  document.addEventListener('click', e => {
    const pill = e.target.closest('.bs-cv-pill');
    if (!pill) return;
    e.preventDefault();
    selectValue(pill);
  });

  // Keyboard: native <button type="button"> already handles Enter/Space and
  // Tab focus order with no extra JS — nothing further needed here.

  return { resolveVariant, valueAvailability };
})();

/* ─────────────────────────────────────────
   VARIANT MODAL
───────────────────────────────────────── */
const BSVariantModal = (() => {
  let currentBtn = null;
  let selectedVariantId = null;

  async function open(triggerBtn) {
    currentBtn = triggerBtn;
    selectedVariantId = null;

    const modal    = document.getElementById('bs-variant-modal');
    const titleEl  = document.getElementById('bs-vm-title');
    const priceEl  = document.getElementById('bs-vm-price');
    const optsEl   = document.getElementById('bs-vm-options');
    const imgWrap  = document.getElementById('bs-vm-img-wrap');
    if (!modal) return;

    // Fetch product variants via AJAX
    const handle = triggerBtn.dataset.productHandle;
    if (!handle) return;

    try {
      const data = await shopifyFetch(bsApiUrl(`/products/${handle}.js`));
      const title = data.title;

      if (titleEl) titleEl.textContent = title;
      if (priceEl) priceEl.innerHTML = money(data.price);

      // Product image
      if (imgWrap) {
        imgWrap.innerHTML = data.featured_image
          ? `<img src="${data.featured_image}" alt="${esc(title)}" width="56" height="56" loading="lazy">`
          : '';
      }

      // Variant options
      if (optsEl) {
        optsEl.innerHTML = data.variants.map(v => `
          <button class="bs-variant-pill${!v.available ? ' bs-variant-pill--disabled' : ''}"
                  data-variant-id="${v.id}"
                  data-variant-price="${v.price}"
                  ${!v.available ? 'disabled' : ''}>
            ${esc(v.title)}${!v.available ? ' <small style="opacity:.55">— ' + ((window.BSLocale && window.BSLocale.product && window.BSLocale.product.soldOut) || 'Sold Out') + '</small>' : ''}
          </button>
        `).join('');

        // Select first available
        const firstAvail = data.variants.find(v => v.available);
        if (firstAvail) selectVariant(optsEl.querySelector(`[data-variant-id="${firstAvail.id}"]`));
      }

      modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      BSNovaCurrency.notifyDomUpdated(modal);
    } catch(e) {
      console.error('BSVariantModal.open', e);
    }
  }

  function selectVariant(btn) {
    if (!btn) return;
    document.querySelectorAll('#bs-vm-options .bs-variant-pill').forEach(p => p.classList.remove('active'));
    btn.classList.add('active');
    selectedVariantId = btn.dataset.variantId;
    const priceEl = document.getElementById('bs-vm-price');
    if (priceEl && btn.dataset.variantPrice) {
      priceEl.innerHTML = money(parseInt(btn.dataset.variantPrice, 10));
      BSNovaCurrency.notifyDomUpdated(priceEl);
    }
  }

  function close() {
    const modal = document.getElementById('bs-variant-modal');
    if (modal) modal.setAttribute('hidden', '');
    document.body.style.overflow = '';
    currentBtn = null;
    selectedVariantId = null;
  }

  async function addSelected() {
    const L = window.BSLocale && window.BSLocale.product || {};
    if (!selectedVariantId) { alert(L.pleaseSelectOption || 'Please select an option.'); return; }
    const title = currentBtn?.dataset.productTitle || L.genericItem || 'Item';
    const addBtn = document.getElementById('bs-vm-add-btn');
    close();
    await BSQuickAdd.addVariant(selectedVariantId, 1, currentBtn, title);
  }

  // Delegated click on variant pills in modal
  document.addEventListener('click', e => {
    const pill = e.target.closest('#bs-vm-options .bs-variant-pill');
    if (pill) selectVariant(pill);
  });

  return { open, close, addSelected };
})();

/* ─────────────────────────────────────────
   GAME SELECTION MODAL
───────────────────────────────────────── */
const BSModal = (() => {
  function open() {
    const modal = document.getElementById('bs-game-modal');
    if (modal) { modal.removeAttribute('hidden'); document.body.style.overflow = 'hidden'; }
  }
  function close() {
    const modal = document.getElementById('bs-game-modal');
    if (modal) { modal.setAttribute('hidden', ''); document.body.style.overflow = ''; }
  }
  document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
  return { open, close };
})();

/* ─────────────────────────────────────────
   HEADER DROPDOWN
───────────────────────────────────────── */
const BSDropdown = (() => {
  function init() {
    const btn  = document.getElementById('bs-game-btn');
    const menu = document.getElementById('bs-game-menu');
    const wrap = document.querySelector('.bs-dropdown');
    if (!btn || !menu) return;

    btn.addEventListener('click', e => {
      e.stopPropagation();
      const open = menu.classList.toggle('open');
      wrap?.toggleAttribute('data-open', open);
      btn.setAttribute('aria-expanded', String(open));
      menu.setAttribute('aria-hidden', String(!open));
    });

    document.addEventListener('click', () => {
      menu.classList.remove('open');
      wrap?.removeAttribute('data-open');
      btn.setAttribute('aria-expanded', 'false');
      menu.setAttribute('aria-hidden', 'true');
    });

    menu.addEventListener('click', e => e.stopPropagation());
  }
  return { init };
})();

/* ─────────────────────────────────────────
   LANGUAGE SELECTOR
   Every listener here is delegated from document, so the module survives a
   shopify:section:load re-render of the header without re-initialising —
   unlike BSDropdown above, which binds to ids once on DOMContentLoaded.
   The selector itself needs none of this: <details> opens and the submit
   buttons switch language with JavaScript switched off entirely. This only
   adds close-on-outside-click, Escape, arrow-key roving focus, and the
   bs_language_preference cookie that records an explicit customer choice.
───────────────────────────────────────── */
const BSLangSelector = (() => {
  const ROOT = 'details[data-bs-lang-selector]';

  function all() {
    return Array.prototype.slice.call(document.querySelectorAll(ROOT));
  }

  function close(el, refocus) {
    if (!el || !el.open) return;
    el.open = false;
    if (refocus) {
      const summary = el.querySelector('summary');
      if (summary && summary.focus) summary.focus();
    }
  }

  function closeOthers(keep) {
    all().forEach(el => { if (el !== keep) close(el, false); });
  }

  function options(el) {
    return Array.prototype.slice
      .call(el.querySelectorAll('.bs-lang-selector__option'))
      .filter(btn => !btn.disabled);
  }

  function rememberChoice(code) {
    if (!code) return;
    try {
      document.cookie = 'bs_language_preference=' + encodeURIComponent(code) +
        ';path=/;max-age=31536000;samesite=lax';
    } catch (e) { /* a blocked cookie jar must not stop the language switch */ }
  }

  // Capture phase: the header's game dropdown calls stopPropagation() on its
  // own trigger, so a bubbling listener would never see that click and the
  // language panel would stay open behind the game menu.
  document.addEventListener('click', e => {
    const target = e.target;
    if (!target || !target.closest) { closeOthers(null); return; }

    const chosen = target.closest('[data-bs-lang-code]');
    if (chosen) rememberChoice(chosen.getAttribute('data-bs-lang-code'));

    const summary = target.closest(ROOT + ' > summary');
    const inside = target.closest(ROOT);
    closeOthers(summary ? summary.parentNode : inside);
  }, true);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      const open = all().filter(el => el.open);
      if (!open.length) return;
      open.forEach(el => close(el, true));
      return;
    }

    const inside = e.target && e.target.closest ? e.target.closest(ROOT) : null;
    if (!inside || !inside.open) return;

    const list = options(inside);
    if (!list.length) return;
    const current = list.indexOf(e.target);
    let next = -1;
    if (e.key === 'ArrowDown') next = current < 0 ? 0 : (current + 1) % list.length;
    else if (e.key === 'ArrowUp') next = current <= 0 ? list.length - 1 : current - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = list.length - 1;
    if (next < 0) return;
    e.preventDefault();
    if (list[next].focus) list[next].focus();
  });

  return { close, closeOthers, options, rememberChoice };
})();
window.BSLangSelector = BSLangSelector;

/* ─────────────────────────────────────────
   MOBILE MENU
───────────────────────────────────────── */
const BSMobileMenu = (() => {
  // Must match the breakpoint in bloxshop.css where .bs-burger becomes visible;
  // above it the drawer is unreachable and must never be left open or locking
  // the body after a resize.
  const BREAKPOINT = 900;
  let lastFocused = null;

  function parts() {
    return {
      burger: document.getElementById('bs-burger'),
      menu:   document.getElementById('bs-mobile-menu')
    };
  }

  function isOpen() {
    const { menu } = parts();
    return !!menu && menu.classList.contains('open');
  }

  /* The drawer sits in normal flow under a sticky header whose height is not a
     constant: it changes with the announcement bar, the logo size and whether
     the nav row wraps. Measuring the drawer's own top offset is exact.
     Subtracting an assumed header height was not — `calc(100dvh - 96px)`
     assumed 96px where the real offset is ~110px, so the drawer ran 14px past
     the fold at 430x568 (D-48).

     visualViewport is preferred over innerHeight because it excludes the
     mobile browser's own collapsing toolbars, which is the height the user can
     actually see. Measured inside requestAnimationFrame so the style/layout
     change from adding `.open` has been applied first — reading in the same
     tick returns the pre-open box. */
  const DRAWER_BOTTOM_GAP = 8;
  const DRAWER_MIN = 160;

  // Not every host that runs this file has a rendering loop — the mobile-nav test
  // harness evaluates it in plain Node, where requestAnimationFrame does not exist.
  // Degrade instead of throwing: the measurement is an enhancement over the CSS
  // fallback, never a precondition for the drawer opening.
  function afterPaint(fn) {
    if (typeof requestAnimationFrame === 'function') return requestAnimationFrame(fn);
    if (typeof setTimeout === 'function') return setTimeout(fn, 0);
    return fn();
  }

  function measure() {
    const { menu } = parts();
    if (!menu || !menu.classList.contains('open')) return;
    if (typeof menu.getBoundingClientRect !== 'function' || !menu.style) return;
    afterPaint(() => {
      if (!menu.classList.contains('open')) return;
      const vv = typeof window !== 'undefined' ? window.visualViewport : null;
      const vh = vv ? vv.height : (typeof window !== 'undefined' ? window.innerHeight : 0);
      if (!vh) return;
      const top = menu.getBoundingClientRect().top;
      const avail = Math.max(DRAWER_MIN, Math.round(vh - top - DRAWER_BOTTOM_GAP));
      menu.style.setProperty('--bs-drawer-max', avail + 'px');
    });
  }

  function open() {
    const { burger, menu } = parts();
    if (!burger || !menu || menu.classList.contains('open')) return;
    lastFocused = document.activeElement;
    menu.classList.add('open');
    menu.removeAttribute('aria-hidden');
    // The drawer collapses via max-height, so its links stay in the tab order
    // unless it is explicitly inert while closed.
    menu.removeAttribute('inert');
    burger.classList.add('open');
    burger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    measure();
    const first = menu.querySelector('a[href], select, button, input');
    if (first) first.focus();
  }

  // restoreFocus is skipped when the drawer closes because the user is
  // navigating away or tapping elsewhere — moving focus back to the burger
  // would fight what they just clicked.
  function close(restoreFocus) {
    const { burger, menu } = parts();
    if (!burger || !menu || !menu.classList.contains('open')) return;
    menu.classList.remove('open');
    if (menu.style) menu.style.removeProperty('--bs-drawer-max');
    menu.setAttribute('aria-hidden', 'true');
    menu.setAttribute('inert', '');
    burger.classList.remove('open');
    burger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (restoreFocus !== false) {
      const target = (lastFocused && document.contains(lastFocused)) ? lastFocused : burger;
      if (target && typeof target.focus === 'function') target.focus();
    }
    lastFocused = null;
  }

  function toggle() { if (isOpen()) { close(); } else { open(); } }

  function init() {
    const { burger, menu } = parts();
    if (!burger || !menu) return;
    if (!menu.classList.contains('open')) menu.setAttribute('inert', '');

    burger.addEventListener('click', e => { e.stopPropagation(); toggle(); });

    // A tap on any drawer link closes it, so it never covers the page that link
    // just navigated to (in-page anchors would otherwise leave it open).
    menu.addEventListener('click', e => {
      if (e.target.closest('a[href]')) close(false);
    });

    document.addEventListener('click', e => {
      if (!isOpen()) return;
      if (menu.contains(e.target) || burger.contains(e.target)) return;
      close(false);
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && isOpen()) close();
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth > BREAKPOINT && isOpen()) close(false);
      else measure();
    });

    // Rotating the device and the mobile browser collapsing its toolbars both
    // change the usable height without firing a useful `resize` on every
    // engine, so re-measure from the viewport's own events too.
    window.addEventListener('orientationchange', measure);
    if (window.visualViewport && typeof window.visualViewport.addEventListener === 'function') {
      window.visualViewport.addEventListener('resize', measure);
    }
  }

  return { open, close, toggle, isOpen, init };
})();

/* ─────────────────────────────────────────
   SEARCH
───────────────────────────────────────── */
const BSSearch = (() => {
  let debounce;
  let requestVersion = 0;

  function open() {
    // Same shared-scroll-lock reason as BSCart.open().
    BSMobileMenu.close(false);
    const overlay = document.getElementById('bs-search-overlay');
    if (overlay) { overlay.removeAttribute('hidden'); document.body.style.overflow = 'hidden'; }
    const input = document.getElementById('bs-search-input');
    if (input) search(input.value);
    setTimeout(() => document.getElementById('bs-search-input')?.focus(), 80);
  }

  function close() {
    clearTimeout(debounce);
    requestVersion++;
    const overlay = document.getElementById('bs-search-overlay');
    if (overlay) overlay.setAttribute('hidden', '');
    document.body.style.overflow = '';
  }

  async function search(q) {
    const resultsEl = document.getElementById('bs-search-results');
    if (!resultsEl) return;
    const version = ++requestVersion;
    const LS = (window.BSLocale && window.BSLocale.search) || {};
    const searchUrl = bsLocaleUrl((window.BSLocale && window.BSLocale.routes && window.BSLocale.routes.search) || '/search');
    q = q.trim();
    if (q.length < 2) { resultsEl.innerHTML = ''; return; }

    resultsEl.innerHTML = '<div class="bs-search-empty" style="padding:20px 0;"><div class="bs-spinner bs-spinner--blue" style="margin:0 auto;"></div></div>';

    try {
      // Use plain fetch (no Content-Type header) — shopifyFetch sends Content-Type:application/json
      // which causes Shopify's predictive search to return HTML instead of JSON on GET requests
      const resp = await fetch(
        bsApiUrl('/search/suggest.json') + '?q=' + encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=8',
        { headers: { 'Accept': 'application/json' } }
      );
      if (!resp.ok) {
        const error = new Error('Predictive search HTTP ' + resp.status);
        error.status = resp.status;
        throw error;
      }
      const data = await resp.json();
      if (version !== requestVersion) return;

      const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];

      if (!products.length) {
        resultsEl.innerHTML =
          '<div class="bs-search-empty">' + (LS.noResultsFor || 'No results for <strong>__Q__</strong>').replace('__Q__', esc(q)) +
          ' &mdash; <a href="' + searchUrl + '?q=' + encodeURIComponent(q) + '">' + (LS.fullSearch || 'Full search') + '</a></div>';
        return;
      }

      resultsEl.innerHTML = products.map(function(p) {
        // image can be a URL string or an object with .url/.src depending on Shopify version
        var imgSrc = '';
        if (p.image) {
          imgSrc = typeof p.image === 'string' ? p.image : (p.image.src || p.image.url || '');
        } else if (p.featured_image) {
          imgSrc = typeof p.featured_image === 'string' ? p.featured_image : (p.featured_image.url || '');
        }
        // p.price is a string like "9.99" — convert to Shopify money format
        var priceStr = '';
        if (p.price) {
          var cents = parseFloat(p.price) * 100;
          priceStr = money(isNaN(cents) ? 0 : cents);
        }
        return '<a href="' + esc(bsLocaleUrl(p.url)) + '" class="bs-search-result-item" onclick="BSSearch.close()">' +
          '<div class="bs-search-result-item__img">' +
            (imgSrc ? '<img src="' + imgSrc + '" alt="' + esc(p.title) + '" width="52" height="52" loading="lazy">' : '') +
          '</div>' +
          '<div class="bs-search-result-item__body">' +
            '<p class="bs-search-result-item__title">' + esc(p.title) + '</p>' +
            (priceStr ? '<p class="bs-search-result-item__price">' + priceStr + '</p>' : '') +
          '</div>' +
        '</a>';
      }).join('') +
      '<a href="' + searchUrl + '?q=' + encodeURIComponent(q) + '" class="bs-search-view-all" onclick="BSSearch.close()">' + (LS.viewAllResults || 'View all results →') + '</a>';
      BSNovaCurrency.notifyDomUpdated(resultsEl);

    } catch(e) {
      console.warn('BSSearch request failed', e);
      if (version !== requestVersion) return;
      resultsEl.innerHTML =
        '<div class="bs-search-empty">' + (LS.unavailable || 'Search unavailable.') + ' ' +
        '<a href="' + searchUrl + '?q=' + encodeURIComponent(q) + '">' + (LS.tryFullSearch || 'Try full search') + '</a></div>';
    }
  }

  function init() {
    const input = document.getElementById('bs-search-input');
    if (input) {
      input.addEventListener('input', () => {
        clearTimeout(debounce);
        requestVersion++;
        const results = document.getElementById('bs-search-results');
        if (results) results.innerHTML = '';
        debounce = setTimeout(() => search(input.value), 260);
      });
    }
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
  }

  return { open, close, init };
})();

/* ─────────────────────────────────────────
   FAQ ACCORDION
───────────────────────────────────────── */
const BSFAQ = (() => {
  function init() {
    document.querySelectorAll('[data-faq-trigger]').forEach(btn => {
      btn.addEventListener('click', () => {
        const answer  = btn.nextElementSibling;
        const isOpen  = btn.getAttribute('aria-expanded') === 'true';

        // Close all others
        document.querySelectorAll('[data-faq-trigger][aria-expanded="true"]').forEach(b => {
          if (b !== btn) {
            b.setAttribute('aria-expanded', 'false');
            b.nextElementSibling?.setAttribute('hidden', '');
            b.nextElementSibling?.style && (b.nextElementSibling.style.maxHeight = '0');
          }
        });

        btn.setAttribute('aria-expanded', String(!isOpen));
        if (isOpen) {
          answer?.setAttribute('hidden', '');
          if (answer) answer.style.maxHeight = '0';
        } else {
          answer?.removeAttribute('hidden');
          if (answer) answer.style.maxHeight = answer.scrollHeight + 'px';
        }
      });
    });
  }
  return { init };
})();

/* ─────────────────────────────────────────
   CART CURRENCY & PAYMENT DISCLOSURE
───────────────────────────────────────── */
const BSCartDisclosure = (() => {
  function init() {
    document.querySelectorAll('[data-cart-disclosure-trigger]').forEach(btn => {
      if (btn.dataset.bsBound) return;
      btn.dataset.bsBound = '1';
      btn.addEventListener('click', () => {
        const panel  = btn.nextElementSibling;
        const isOpen = btn.getAttribute('aria-expanded') === 'true';

        btn.setAttribute('aria-expanded', String(!isOpen));
        if (isOpen) {
          panel?.setAttribute('hidden', '');
          if (panel) panel.style.maxHeight = '0';
        } else {
          panel?.removeAttribute('hidden');
          if (panel) panel.style.maxHeight = panel.scrollHeight + 'px';
        }
      });
    });
  }
  return { init };
})();

/* ─────────────────────────────────────────
   PRODUCT PAGE
───────────────────────────────────────── */
const BSProduct = (() => {
  function switchImage(src, thumbEl) {
    const img = document.getElementById('bs-product-main-img');
    if (img) img.src = src;
    document.querySelectorAll('.bs-product-page__thumb').forEach(t => t.classList.remove('active'));
    if (thumbEl) thumbEl.classList.add('active');
  }

  function adjustQty(delta) {
    const input = document.getElementById('bs-qty');
    if (!input) return;
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
  }

  function selectOption(btn, optionIndex) {
    const group = btn.closest('.bs-variant-pills');
    if (group) {
      group.querySelectorAll('.bs-variant-pill').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
    }

    // Find matching variant
    if (!window.BSProductData?.variants) return;
    const selected = [];
    document.querySelectorAll('.bs-variant-pills').forEach((g, i) => {
      const active = g.querySelector('.bs-variant-pill.active');
      selected[i] = active?.dataset.value;
    });

    const match = window.BSProductData.variants.find(v =>
      v.options.every((opt, i) => !selected[i] || opt === selected[i])
    );

    if (match) {
      const select = document.getElementById('bs-product-select');
      if (select) select.value = match.id;

      // Update ATC button
      const atcBtn = document.getElementById('bs-product-atc');
      if (atcBtn) {
        atcBtn.dataset.variantId = match.id;
        atcBtn.disabled = !match.available;
        const LP = (window.BSLocale && window.BSLocale.product) || {};
        atcBtn.textContent = match.available ? (LP.addToCart || 'Add to Cart') : (LP.soldOut || 'Sold Out');
      }

      // Update price display
      const priceEl = document.querySelector('.bs-product-page__price .bs-price');
      if (priceEl) {
        priceEl.innerHTML = money(match.price);
        BSNovaCurrency.notifyDomUpdated(priceEl);
      }
    }
  }

  return { switchImage, adjustQty, selectOption };
})();

/* ─────────────────────────────────────────
   INIT
───────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  BSCart.refresh();
  BSDropdown.init();
  BSMobileMenu.init();
  BSSearch.init();
  BSFAQ.init();
  BSCartDisclosure.init();
});

/* ==========================================================================
   GAME TABS — moved into sections/game-rows.liquid's own {% javascript %}
   block (Stage 7). This global copy never listened for
   shopify:section:load, so it went dead the moment the theme editor
   re-rendered the section through the Section Rendering API — every tab
   except whichever one was server-rendered as already-active appeared to
   do nothing when clicked. See game-rows.liquid for the fix.
   ========================================================================== */

/* ==========================================================================
   QUICK-ADD: extend to handle bs-qadd-full buttons
   The existing BSQuickAdd already uses event delegation on [data-quick-add],
   so bs-qadd-full buttons work automatically. We just need spinner/success
   states for the labeled button.
   ========================================================================== */
(function() {
  'use strict';

  // Override setBtn to also handle full-width labeled buttons
  var _origSetBtn = (typeof BSQuickAdd !== 'undefined') ? BSQuickAdd.setBtn : null;

  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.bs-qadd-full[data-quick-add]');
    if (!btn || btn.disabled) return;
    // The main BSQuickAdd delegation handles the actual cart logic.
    // Here we just ensure loading/success visual for the full button.
    // Loading: set text
    var originalHTML = btn.innerHTML;
    btn.__originalHTML = originalHTML;
  }, true); // capture phase so we run before BSQuickAdd

  // Patch BSQuickAdd.setBtn if available
  function patchSetBtn() {
    if (typeof BSQuickAdd === 'undefined') return;
    var orig = BSQuickAdd.setBtn;
    BSQuickAdd.setBtn = function(btn, state, label) {
      if (!btn) return;
      // Full-width labeled button
      if (btn.classList.contains('bs-qadd-full')) {
        var states = {
          idle:    btn.__originalHTML || '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="white" stroke-width="2" stroke-linecap="round"/><line x1="3" y1="6" x2="21" y2="6" stroke="white" stroke-width="2"/><path d="M16 10a4 4 0 01-8 0" stroke="white" stroke-width="2" stroke-linecap="round"/></svg> Add to Cart',
          loading: '<div class="bs-spinner" style="width:14px;height:14px;border-width:2px;margin:0 auto;"></div>',
          success: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 13l4 4L19 7" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg> Added!'
        };
        if (!btn.__originalHTML && state === 'loading') btn.__originalHTML = btn.innerHTML;
        btn.innerHTML = states[state] || states.idle;
        btn.disabled  = state === 'loading';
        btn.classList.toggle('bs-qadd-full--loading',  state === 'loading');
        if (state === 'success') {
          btn.style.background = 'var(--green)';
          setTimeout(function() {
            btn.style.background = '';
            btn.innerHTML = btn.__originalHTML;
          }, 1800);
        } else {
          btn.style.background = '';
        }
        return;
      }
      // Fall back to original for circle buttons
      orig(btn, state, label);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchSetBtn);
  } else {
    setTimeout(patchSetBtn, 0); // after bloxshop.js BSQuickAdd init
  }
})();

/* ==========================================================================
   HERO TEXT ROTATOR — slot-machine style
   ========================================================================== */
(function () {
  'use strict';

  function initRotator() {
    var display = document.getElementById('bs-rotator-display') || document.querySelector('#bs-rotator-words .bs-rotator-word');
    if (!display) return;

    var raw = display.getAttribute('data-words') || '';
    var texts = raw.split(',').map(function(t){ return t.trim(); }).filter(Boolean);
    if (texts.length < 2) return;

    // Pre-measure widest word for stable container width
    var container = document.getElementById('bs-hero-rotator');
    if (container) {
      var maxW = 0;
      texts.forEach(function(t) {
        display.textContent = t;
        var w = display.offsetWidth || display.scrollWidth;
        if (w > maxW) maxW = w;
      });
      if (maxW > 0) container.style.minWidth = (maxW + 4) + 'px';
    }

    display.textContent = texts[0];
    var idx = 0;

    var timer = setInterval(function() {
      idx = (idx + 1) % texts.length;
      display.textContent = texts[idx];
    }, 2000);

    if (container) {
      container.addEventListener('mouseenter', function(){ clearInterval(timer); });
      container.addEventListener('mouseleave', function(){
        timer = setInterval(function(){
          idx = (idx + 1) % texts.length;
          display.textContent = texts[idx];
        }, 2000);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRotator);
  } else {
    initRotator();
  }
})();

/* ==========================================================================
   QUICK-ADD: extend to handle bs-qadd-full buttons
   The existing BSQuickAdd already uses event delegation on [data-quick-add],
   so bs-qadd-full buttons work automatically. We just need spinner/success
   states for the labeled button.
   ========================================================================== */
(function() {
  'use strict';

  // Override setBtn to also handle full-width labeled buttons
  var _origSetBtn = (typeof BSQuickAdd !== 'undefined') ? BSQuickAdd.setBtn : null;

  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.bs-qadd-full[data-quick-add]');
    if (!btn || btn.disabled) return;
    // The main BSQuickAdd delegation handles the actual cart logic.
    // Here we just ensure loading/success visual for the full button.
    // Loading: set text
    var originalHTML = btn.innerHTML;
    btn.__originalHTML = originalHTML;
  }, true); // capture phase so we run before BSQuickAdd

  // Patch BSQuickAdd.setBtn if available
  function patchSetBtn() {
    if (typeof BSQuickAdd === 'undefined') return;
    var orig = BSQuickAdd.setBtn;
    BSQuickAdd.setBtn = function(btn, state, label) {
      if (!btn) return;
      // Full-width labeled button
      if (btn.classList.contains('bs-qadd-full')) {
        var states = {
          idle:    btn.__originalHTML || '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="white" stroke-width="2" stroke-linecap="round"/><line x1="3" y1="6" x2="21" y2="6" stroke="white" stroke-width="2"/><path d="M16 10a4 4 0 01-8 0" stroke="white" stroke-width="2" stroke-linecap="round"/></svg> Add to Cart',
          loading: '<div class="bs-spinner" style="width:14px;height:14px;border-width:2px;margin:0 auto;"></div>',
          success: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 13l4 4L19 7" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg> Added!'
        };
        if (!btn.__originalHTML && state === 'loading') btn.__originalHTML = btn.innerHTML;
        btn.innerHTML = states[state] || states.idle;
        btn.disabled  = state === 'loading';
        btn.classList.toggle('bs-qadd-full--loading',  state === 'loading');
        if (state === 'success') {
          btn.style.background = 'var(--green)';
          setTimeout(function() {
            btn.style.background = '';
            btn.innerHTML = btn.__originalHTML;
          }, 1800);
        } else {
          btn.style.background = '';
        }
        return;
      }
      // Fall back to original for circle buttons
      orig(btn, state, label);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchSetBtn);
  } else {
    setTimeout(patchSetBtn, 0); // after bloxshop.js BSQuickAdd init
  }
})();

/* ==========================================================================
   HERO TEXT ROTATOR — slot-machine style
   ========================================================================== */
(function () {
  'use strict';

  function initRotator() {
    var wordSpans = document.querySelectorAll('#bs-rotator-words .bs-rotator-word');
    if (!wordSpans || wordSpans.length < 2) return;

    // Collect all word texts from existing spans
    var texts = [];
    wordSpans.forEach(function(w) { texts.push(w.textContent.trim()); });

    // KEEP the first existing span visible (it already has gradient CSS applied)
    // just hide spans 2-N and reuse span 0 as the display element
    var display = wordSpans[0];
    for (var i = 1; i < wordSpans.length; i++) {
      wordSpans[i].style.display = 'none';
    }

    // Fix container width to the widest word (measure before hiding)
    var container = document.getElementById('bs-hero-rotator');
    if (container) {
      var maxW = 0;
      // All spans still in DOM — measure each
      for (var i = 0; i < wordSpans.length; i++) {
        wordSpans[i].style.display = 'inline';
        var w = wordSpans[i].offsetWidth || wordSpans[i].scrollWidth || 0;
        if (w > maxW) maxW = w;
        wordSpans[i].style.display = i === 0 ? 'inline' : 'none';
      }
      if (maxW > 0) container.style.minWidth = (maxW + 4) + 'px';
    }

    var idx = 0;
    var timer = setInterval(function() {
      idx = (idx + 1) % texts.length;
      display.textContent = texts[idx];
    }, 2000);

    if (container) {
      container.addEventListener('mouseenter', function () { clearInterval(timer); });
      container.addEventListener('mouseleave', function () { timer = setInterval(function() {
        idx = (idx + 1) % texts.length;
        display.textContent = texts[idx];
      }, 2000); });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRotator);
  } else {
    initRotator();
  }
})();

/* ==========================================================================
   FLOAT CARD ATC — extend BSQuickAdd.setBtn for .bs-float-card__atc
   ========================================================================== */
(function () {
  'use strict';

  function patchFloatCardBtn() {
    if (typeof BSQuickAdd === 'undefined') return;
    var orig = BSQuickAdd.setBtn;
    BSQuickAdd.setBtn = function (btn, state, label) {
      if (!btn) return;
      if (btn.classList.contains('bs-float-card__atc')) {
        var saved = btn.__savedHTML || btn.innerHTML;
        if (state === 'loading') {
          btn.__savedHTML = saved;
          btn.innerHTML   = '<div class="bs-spinner" style="width:12px;height:12px;border-width:2px;margin:0 auto;"></div>';
          btn.disabled    = true;
        } else if (state === 'success') {
          btn.innerHTML = '✓ Added!';
          btn.disabled  = false;
          btn.style.background = 'var(--green)';
          setTimeout(function () {
            btn.innerHTML = saved;
            btn.style.background = '';
          }, 1800);
        } else {
          btn.innerHTML = saved;
          btn.disabled  = false;
          btn.style.background = '';
        }
        return;
      }
      orig(btn, state, label);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchFloatCardBtn);
  } else {
    setTimeout(patchFloatCardBtn, 0);
  }
})();

/* ==========================================================================
   CAROUSEL — prev/next arrows, scroll-based opacity, re-init for dynamic content
   ========================================================================== */
(function () {
  'use strict';

  function initCarousel(el) {
    if (el.__carouselInit) return;
    el.__carouselInit = true;

    var track = el.querySelector('.bs-carousel__track');
    var prev  = el.querySelector('.bs-carousel__prev');
    var next  = el.querySelector('.bs-carousel__next');
    if (!track) return;

    function scrollAmt() {
      var card = track.querySelector('.bs-product-card');
      return card ? (card.offsetWidth + 18) * 2 : 340;
    }
    // RTL scrollLeft is 0 at the start and goes negative toward the end
    // (CSSOM View "negative" convention), so prev/next must flip sign.
    function isRtl() {
      return getComputedStyle(track).direction === 'rtl';
    }
    if (prev) prev.addEventListener('click', function () {
      track.scrollBy({ left: isRtl() ? scrollAmt() : -scrollAmt(), behavior: 'smooth' });
    });
    if (next) next.addEventListener('click', function () {
      track.scrollBy({ left: isRtl() ? -scrollAmt() : scrollAmt(), behavior: 'smooth' });
    });

    function updateBtns() {
      if (!prev || !next) return;
      var rtl     = isRtl();
      var atStart = rtl ? track.scrollLeft >= -8 : track.scrollLeft <= 8;
      var atEnd   = rtl
        ? track.scrollLeft <= -(track.scrollWidth - track.clientWidth - 8)
        : track.scrollLeft >= track.scrollWidth - track.clientWidth - 8;
      prev.style.opacity = atStart ? '0.35' : '1';
      prev.disabled      = atStart;
      next.style.opacity = atEnd   ? '0.35' : '1';
      next.disabled      = atEnd;
    }
    track.addEventListener('scroll', updateBtns, { passive: true });
    updateBtns();
  }

  function initAll() {
    document.querySelectorAll('[data-carousel]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Expose so new carousels added to DOM can be inited
  window.BSCarousel = { initAll: initAll, init: initCarousel };
})();

/* ==========================================================================
   ANCHOR SMOOTH SCROLL — for collection subnav pills
   ========================================================================== */
(function () {
  'use strict';
  document.addEventListener('click', function (e) {
    var link = e.target.closest('.bs-anchor-link[data-anchor]');
    if (!link) return;
    e.preventDefault();
    var id  = link.getAttribute('data-anchor');
    var el  = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
})();

/* ==========================================================================
   ROBLOX USERNAME NOTE — saves to Shopify cart note (shows in admin orders)
   ========================================================================== */
(function () {
  'use strict';

  var _debounceTimer = null;

  function saveNote(username) {
    var note = username ? ('Roblox Username: ' + username) : '';
    fetch(bsApiUrl('/cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ note: note })
    }).catch(function (e) { console.error('BSCartNote', e); });
  }

  function init() {
    var input = document.getElementById('bs-roblox-username');
    if (!input) return;

    // Restore value across cart opens
    var saved = sessionStorage.getItem('bs_roblox_user') || '';
    if (saved) input.value = saved;

    input.addEventListener('input', function () {
      var val = input.value.trim();
      sessionStorage.setItem('bs_roblox_user', val);
      clearTimeout(_debounceTimer);
      _debounceTimer = setTimeout(function () { saveNote(val); }, 700);
    });

    input.addEventListener('blur', function () {
      clearTimeout(_debounceTimer);
      saveNote(input.value.trim());
    });
  }

  // Run on DOMContentLoaded and re-run after cart opens (input injected lazily)
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('click', function (e) {
    if (e.target.closest('.bs-cart-trigger, [onclick*="BSCart.open"]')) {
      setTimeout(init, 300);
    }
  });
})();

/* ==========================================================================
   SERVICE FEE — auto-add 5% fee product, round up to nearest $0.50
   Settings injected via window.BSServiceFeeSettings in layout/theme.liquid
   ========================================================================== */
var BSServiceFee = (function () {
  'use strict';

  var cfg     = window.BSServiceFeeSettings || {};
  var ENABLED = cfg.enabled !== false;  // default ON unless explicitly disabled
  var HANDLE  = (cfg.handle || 'service-fee').trim();
  var RATE    = parseFloat(cfg.rate) / 100 || 0.05;
  var ROUND   = parseFloat(cfg.round) || 0.5;
  var _lock   = false;
  var _prod   = null;   // cached product JSON

  function isFeeLine(item) { return item.handle === HANDLE; }

  // Calculate fee in cents: 5% of subtotal, rounded up to nearest $0.50
  function calcFeeCents(subtotalCents) {
    var rawDollars     = (subtotalCents / 100) * RATE;
    var roundedDollars = Math.floor(rawDollars / ROUND) * ROUND;
    var feeCents       = Math.round(roundedDollars * 100);
    // Minimum fee of $0.50 — floor can produce $0 for small carts
    return feeCents > 0 ? feeCents : Math.round(ROUND * 100);
  }

  async function getProduct() {
    if (_prod) return _prod;

    // Use server-side injected variants (via Liquid all_products) — no fetch needed
    if (window.BSServiceFeeVariants && window.BSServiceFeeVariants.length > 0) {
      _prod = { variants: window.BSServiceFeeVariants };
      return _prod;
    }
    console.warn('[SF] BSServiceFeeVariants not set - falling back to fetch /products/'+HANDLE+'.js');

    // Fallback: fetch from storefront (requires product published to Online Store)
    try {
      var r = await fetch(bsApiUrl('/products/' + HANDLE + '.js'));
      if (!r.ok) { return null; }
      var data = await r.json();
      if (!data || !data.variants || data.variants.length === 0) { return null; }
      _prod = data;
      return _prod;
    } catch (e) { return null; }
  }

  function findVariant(prod, priceCents) {
    if (!prod || !prod.variants) return null;
    // Shopify storefront /products/handle.js returns price as integer cents (e.g. 50 = $0.50)
    // Guard against string or decimal formats just in case
    return prod.variants.find(function (v) {
      var vp = parseInt(v.price, 10);
      // if stored as dollars decimal (e.g. 0.5 instead of 50), multiply by 100
      if (vp < 10 && priceCents >= 50) vp = Math.round(parseFloat(v.price) * 100);
      return vp === priceCents;
    }) || null;
  }

  // Returns true if cart was actually modified (so caller can re-render)
  async function update(cart) {
    if (!ENABLED || _lock) { return false; }
    _lock = true;

    var modified = false;
    try {
      var feeItems      = cart.items.filter(isFeeLine);
      var feeLine       = feeItems[0] || null;
      var eligibleCents = cart.items.reduce(function (s, i) {
        return isFeeLine(i) ? s : s + i.final_line_price;
      }, 0);

      // Remove duplicates first (all fee lines after the first)
      for (var d = 1; d < feeItems.length; d++) {
        await shopifyFetch(bsApiUrl('/cart/change.js'), {
          method: 'POST',
          body: JSON.stringify({ id: feeItems[d].key, quantity: 0 })
        });
        modified = true;
      }

      // No eligible items → remove fee if present and exit
      if (eligibleCents === 0) {
        if (feeLine) {
          await shopifyFetch(bsApiUrl('/cart/change.js'), {
            method: 'POST',
            body: JSON.stringify({ id: feeLine.key, quantity: 0 })
          });
          modified = true;
        }
        return modified;
      }

      var feeCents = calcFeeCents(eligibleCents);
      var prod     = await getProduct();
      if (!prod) { console.error('[SF] FAIL: could not get fee product. Check handle='+HANDLE); return modified; }

      var variant = findVariant(prod, feeCents);
      if (!variant) {
        console.error('[SF] FAIL: no variant for '+feeCents+' cents. Available:', prod.variants.map(function(v){return v.price;}));
        return modified;
      }

      // Already correct — nothing to do. Injected variant IDs are strings
      // while Shopify's cart line variant_id is a number — normalize both
      // sides so a type mismatch never masquerades as a "wrong variant".
      if (feeLine && String(feeLine.variant_id) === String(variant.id) && feeLine.quantity === 1) {
        return modified;
      }

      // Wrong variant → remove it
      if (feeLine && String(feeLine.variant_id) !== String(variant.id)) {
        await shopifyFetch(bsApiUrl('/cart/change.js'), {
          method: 'POST',
          body: JSON.stringify({ id: feeLine.key, quantity: 0 })
        });
        feeLine = null;
        modified = true;
      }

      // Add correct variant (or fix qty if somehow > 1)
      if (!feeLine) {
        // Use inventory_policy:continue in payload to bypass deny policy on 0-stock products
        var variantId = String(variant.id); // use string to avoid JS int precision issues
        await shopifyFetch(bsApiUrl('/cart/add.js'), {
          method: 'POST',
          body: JSON.stringify({ id: variantId, quantity: 1, properties: { _service_fee: '1' } })
        });
        modified = true;
      } else if (feeLine.quantity !== 1) {
        await shopifyFetch(bsApiUrl('/cart/change.js'), {
          method: 'POST',
          body: JSON.stringify({ id: feeLine.key, quantity: 1 })
        });
        modified = true;
      }
    } catch (e) {
      console.warn('[ServiceFee] Update error:', e);
    } finally {
      _lock = false;
    }
    return modified;
  }

  return {
    isFeeLine    : isFeeLine,
    update       : update,
    handle       : HANDLE,
    enabled      : ENABLED,
    calcFeeCents : calcFeeCents
  };
}());

/* ==========================================================================
   DISCOUNT CODE — stores code, patches checkout URL, shows in cart
   ========================================================================== */
var BSDiscount = (function () {
  'use strict';
  var KEY = 'bs_discount_code';

  function getCode() { return localStorage.getItem(KEY) || ''; }
  function setCode(v) { if (v) localStorage.setItem(KEY, v); else localStorage.removeItem(KEY); }

  function apply() {
    var input = document.getElementById('bs-discount-input');
    var msg   = document.getElementById('bs-discount-msg');
    if (!input) return;
    var code = input.value.trim().toUpperCase();
    setCode(code);
    if (msg) {
      if (code) {
        var savedTpl = (window.BSLocale && window.BSLocale.cart && window.BSLocale.cart.discountSaved) || 'Code "__CODE__" saved — applied at checkout if valid.';
        msg.textContent = savedTpl.replace('__CODE__', code);
        msg.className = 'bs-cart-discount__msg bs-cart-discount__msg--ok';
      } else {
        msg.textContent = '';
      }
    }
  }

  function checkout() {
    var code = getCode();
    if (code) {
      window.location.href = bsLocaleUrl('/discount/' + encodeURIComponent(code)) + '?redirect=' + encodeURIComponent(bsLocaleUrl('/checkout'));
    } else {
      window.location.href = bsLocaleUrl('/checkout');
    }
  }

  // Restore saved code when cart opens
  function restore() {
    var input = document.getElementById('bs-discount-input');
    var code  = getCode();
    if (input && code) {
      input.value = code;
      var msg = document.getElementById('bs-discount-msg');
      if (msg) {
        var savedTpl = (window.BSLocale && window.BSLocale.cart && window.BSLocale.cart.discountSaved) || 'Code "__CODE__" saved — applied at checkout if valid.';
        msg.textContent = savedTpl.replace('__CODE__', code);
        msg.className = 'bs-cart-discount__msg bs-cart-discount__msg--ok';
      }
    }
  }

  document.addEventListener('DOMContentLoaded', restore);

  return { apply: apply, checkout: checkout, restore: restore, getCode: getCode };
}());

/* ==========================================================================
   Global event delegation — CSP-safe replacements for inline onclick
   ========================================================================== */
document.addEventListener('DOMContentLoaded', function() {
  // Discount Apply button
  document.addEventListener('click', function(e) {
    if (e.target.closest('#bs-discount-apply-btn')) {
      e.preventDefault();
      if (typeof BSDiscount !== 'undefined') BSDiscount.apply();
    }
    // Checkout handled per-render in setCartState, this is belt+suspenders
    if (e.target.closest('#bs-checkout-btn')) {
      e.preventDefault();
      if (typeof BSDiscount !== 'undefined') BSDiscount.checkout();
      else window.location.href = bsLocaleUrl('/checkout');
    }
  });
});
